import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, from, Observable, of, tap, throwError } from 'rxjs';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { Router } from '@angular/router';
import { LocalStorageService } from "../../services/local-storage.service";
import { environment } from "../../../environments/environment";
import { Auth, authState, createUserWithEmailAndPassword, GoogleAuthProvider, signInWithEmailAndPassword, signInWithPopup, signOut } from '@angular/fire/auth';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { TermsDialogComponent } from 'app/modules/auth/sign-in/terms-dialog/terms-dialog.component';

@Injectable()
export class AuthService {
    private _authenticated: boolean = false;
    role$ = new BehaviorSubject<any>(null);
    router: any;

    userSubscription: any;

    /**
     * Constructor
     */
    constructor(
        private _userService: UserService,
        private localstorageService: LocalStorageService,
        private _router: Router,
        private auth: Auth,
        private httpClient: HttpClient,
        private _dialog: MatDialog

    ) {
    }

    get roles() {
        const userData = this._userService.userData;
        if (userData && userData.roles) {
            return userData.roles;
        } else {
            return null;
        }
    }


    /**___FIREBASE LOGIN___**/
    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        this.localstorageService.setItem(environment.common.storageKeys.authToken, token);
    }

    get accessToken(): string {
        return this.localstorageService.getItem(environment.common.storageKeys.authToken) ?? '';
    }


    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        if (this._authenticated) {
            return of(true);
        }

        // Check the access token availability
        const tokenFromStorage = this.localstorageService.getItem(environment.common.storageKeys.authToken);
        if (!tokenFromStorage) {
            return of(false);
        }

        // // Check the access token expire date
        if (AuthUtils.isTokenExpired(tokenFromStorage)) {
            return of(false);
        }

        return of(true);
    }


    register(email: string, password: string): Observable<any> {
        return from(createUserWithEmailAndPassword(this.auth, email, password));
    }


    login(email: string, password: string): Observable<any> {
        return from(
            signInWithEmailAndPassword(this.auth, email, password).then(async (credential) => {
                const token = await credential.user.getIdToken(); // Get the Firebase ID token
                return this.httpClient.post(`auth/email-login-call`, { token }).toPromise(); // Send token to backend
            })
        ).pipe(
            tap((resFromHttpLogin: any) => {
                if (resFromHttpLogin.user) {

                    const roles = resFromHttpLogin.user.roles ? resFromHttpLogin.user.roles : [];


                    if (roles.includes('Partner SSE')) {
                        const ndaSigned = resFromHttpLogin.user.ndaSigned;

                        if (!ndaSigned) {

                            // Apri il dialog per i termini
                            const dialogRef = this._dialog.open(TermsDialogComponent, {
                                width: '80vw',
                                height: '90vh',
                                autoFocus: false, // Disable automatic focus
                                restoreFocus: false, // Prevent restoring focus after closing
                                disableClose: true, // Non permette di chiudere cliccando fuori dal dialog
                            });

                            // Gestisci la risposta dal dialog
                            dialogRef.afterClosed().subscribe((result) => {
                                if (result) {
                                    // L'utente ha accettato

                                    this._userService.updateUserNdaSigned(
                                        resFromHttpLogin.token,
                                        true
                                    ).subscribe(res => {
                                        console.log(res);

                                        this._userService.user = resFromHttpLogin.user;
                                        this.role$.next(roles);
                                        this._router.navigate(['/sse2']);
                                    })


                                } else {
                                    // L'utente ha rifiutato
                                    this.logout();
                                    this._router.navigate(['/sign-in']); // Ritorna alla login
                                }
                            });
                        } else {
                            this._userService.user = resFromHttpLogin.user;
                            this.role$.next(roles);
                            this._router.navigate(['/sse2']);
                        }

                    } else {
                        this._userService.user = resFromHttpLogin.user;
                        this.role$.next(roles);
                        this._router.navigate(['/call-overview']);
                    }
                }
            }),
            catchError((error) => {
                console.error('[AuthService] Email login failed', {
                    code: error?.code,
                    message: error?.message,
                    status: error?.status,
                    error: error?.error,
                    fullError: error,
                });

                return throwError(() => error);
            })
        );
    }



    // Google Login
    googleLogin(): Observable<any> {
        const provider = new GoogleAuthProvider();
        return from(
            signInWithPopup(this.auth, provider).then(async (credential) => {
                const token = await credential.user.getIdToken();  // Get the Firebase ID token
                return this.httpClient.post(`auth/google-login-call`, { token }).toPromise(); // Send token to backend
            })
        ).pipe(
            tap((resFromHttpLogin) => {
                // Esegui l'istruzione dopo aver ricevuto la risposta
                if (resFromHttpLogin.user) {
                    this._userService.user = resFromHttpLogin.user;
                    this.role$.next(resFromHttpLogin.user.roles ? resFromHttpLogin.user.roles : []);
                }
            })
        );
    }
    // Logout
    logout(): Observable<void> {
        return from(signOut(this.auth));
    }

    // Get the currently logged-in user
    getCurrentUser(): Observable<any> {
        return authState(this.auth);
    }

    getToken(): Observable<string> {
        const currentUser = this.auth.currentUser;
        return from(currentUser ? currentUser.getIdToken() : Promise.resolve(''));
    }

    signOut() {
        this.accessToken = null;
        this.localstorageService.removeItem(environment.common.storageKeys.authToken);


        // Set the authenticated flag to true
        this._authenticated = false;

        this._userService.user = null;
        this.role$.next(null);
        this.localstorageService.removeItem(environment.common.storageKeys.authData);
        this._router.navigate(['/sign-in'])

    }



    hasRole(roles: string[]): boolean {
        console.log(roles);
        if (this.roles) {
            return roles.some(r => this.roles.map(role => role.toLowerCase().trim()).includes(r.toLowerCase().trim()));
        }
        return false;
    }

}
